// Embedded documentation viewer.
//
// Renders the in-app docs (bundled markdown) with a sidebar. No external links
// out to GitHub — the docs ship with the app. Reached from the header menu
// (Documentation / Learn About QROS / Terms of Service) and from /docs routes.

import { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import changelog from '@/docs/changelog.md?raw';
import userGuide from '@/docs/user-guide.md?raw';
import learnAboutQros from '@/docs/learn-about-qros.md?raw';
import queryingWithQql from '@/docs/querying-with-qql.md?raw';
import llmEvaluationAndTraining from '@/docs/llm-evaluation-and-training.md?raw';
import termsOfService from '@/docs/terms-of-service.md?raw';
// Read from the public developers' folder itself, so the guide an agent's
// builder follows in the app is the one published, not a copy that drifts.
import integrationApi from '../../../Documentation/public/developers/INTEGRATION_API.md?raw';
import { cn } from '@/lib/utils';

interface Doc {
  slug: string;
  title: string;
  content: string;
}

const DOCS: Doc[] = [
  { slug: 'changelog', title: "What's New", content: changelog },
  { slug: 'user-guide', title: 'User Guide', content: userGuide },
  { slug: 'learn-about-qros', title: 'Learn About QROS', content: learnAboutQros },
  { slug: 'querying-with-qql', title: 'Querying with QQL', content: queryingWithQql },
  {
    slug: 'llm-evaluation-and-training',
    title: 'LLM Evaluation & Training',
    content: llmEvaluationAndTraining,
  },
  { slug: 'integration-api', title: 'Connecting Tools and Agents', content: integrationApi },
  { slug: 'terms-of-service', title: 'Terms of Use', content: termsOfService },
];

const DEFAULT_SLUG = 'user-guide';

/** Slugify heading text to a stable anchor id (matches the in-page TOC links). */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function headingText(children: React.ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(headingText).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    const props = (children as { props?: { children?: React.ReactNode } }).props;
    return headingText(props?.children);
  }
  return '';
}

export function DocsPage() {
  const { slug } = useParams();
  const location = useLocation();
  const active = DOCS.find((d) => d.slug === slug) ?? DOCS.find((d) => d.slug === DEFAULT_SLUG)!;

  // Scroll to the hash target (in-page anchor) after the doc renders, and to
  // the top when switching docs without a hash.
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [active.slug, location.hash]);

  const heading = (level: 1 | 2 | 3 | 4) =>
    function Heading({ children }: { children?: React.ReactNode }) {
      const id = slugify(headingText(children));
      const Tag = `h${level}` as const;
      return <Tag id={id}>{children}</Tag>;
    };

  return (
    <div className="container mx-auto flex gap-8 py-6">
      <aside className="hidden w-56 shrink-0 lg:block">
        <nav className="sticky top-6 space-y-1">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-tres-text-secondary">
            Documentation
          </div>
          {DOCS.map((d) => (
            <Link
              key={d.slug}
              to={`/docs/${d.slug}`}
              className={cn(
                'block rounded-md px-3 py-1.5 text-sm',
                d.slug === active.slug
                  ? 'bg-tres-submenu-bg font-medium text-tres-action'
                  : 'text-tres-text-primary hover:bg-muted',
              )}
            >
              {d.title}
            </Link>
          ))}
        </nav>
      </aside>

      <article className="prose prose-slate max-w-3xl dark:prose-invert prose-headings:scroll-mt-20 prose-a:text-tres-action prose-code:before:content-none prose-code:after:content-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: heading(1),
            h2: heading(2),
            h3: heading(3),
            h4: heading(4),
            a({ href, children }) {
              const target = href ?? '';
              // In-app doc links + pure hash anchors navigate within the SPA.
              if (target.startsWith('/')) {
                return (
                  <Link
                    to={target}
                    onClick={(e) => {
                      // Same-page hash jump: scroll without a full nav.
                      const [path, hash] = target.split('#');
                      if (hash && (path === '' || path === `/docs/${active.slug}`)) {
                        e.preventDefault();
                        document
                          .getElementById(hash)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    {children}
                  </Link>
                );
              }
              if (target.startsWith('#')) {
                return (
                  <a
                    href={target}
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById(target.slice(1))
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    {children}
                  </a>
                );
              }
              // mailto: and any genuinely external link open normally.
              const external = target.startsWith('http');
              return (
                <a
                  href={target}
                  {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {children}
                </a>
              );
            },
          }}
        >
          {active.content}
        </ReactMarkdown>
      </article>
    </div>
  );
}

// Re-export the slug list so the menu/router can reference canonical doc slugs.
export const DOC_SLUGS = DOCS.map((d) => d.slug);
